import { useMemo } from "react";
import type { FormulaContextSpec } from "../context";

interface FunctionListProps {
  context: FormulaContextSpec;
  /** Called with the Monaco snippet to insert (function name plus placeholders). */
  onInsert: (snippet: string) => void;
}

export function FunctionList({ context, onInsert }: FunctionListProps) {
  const grouped = useMemo(() => {
    const byCat = new Map<string, FormulaContextSpec["functions"]>();
    for (const fn of context.functions) {
      const cat = fn.category ?? "Other";
      const list = byCat.get(cat) ?? [];
      list.push(fn);
      byCat.set(cat, list);
    }
    return Array.from(byCat.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [context.functions]);

  return (
    <div className="flex flex-col divide-y divide-olive-4">
      {grouped.map(([category, fns]) => (
        <div key={category}>
          <div className="px-2 py-1 text-xs uppercase tracking-wide text-olive-10 bg-olive-3">
            {category}
          </div>
          {fns.map((fn) => (
            <button
              key={fn.name}
              type="button"
              className="w-full text-left px-3 py-1 hover:bg-olive-3 flex flex-col gap-0.5"
              onClick={() => {
                // Emit Tab-navigable placeholders so the arg names don't land as
                // literal text the user has to delete.
                const args = fn.parameters
                  .map((p, i) => `\${${i + 1}:${p.name}}`)
                  .join(", ");
                onInsert(`${fn.name}(${args})`);
              }}
              title={fn.description}
            >
              <span className="font-mono text-sm">
                {fn.name}({fn.parameters.map((p) => p.name).join(", ")})
              </span>
              <span className="text-xs text-olive-10 truncate">
                {fn.description}
              </span>
              {fn.examples && fn.examples.length > 0 && (
                <span className="font-mono text-xs text-olive-9 truncate">
                  e.g. {fn.examples[0]}
                </span>
              )}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
